import * as z from 'zod';

import {
  ContributorProps,
  FundProps,
  MediaProps,
} from '@sola/schema/lib/schema.ts';

export enum osIndexEnum {
  Published = 'published',
  Private = 'private',
  Volunteer = 'volunteer',
}

export const ImageResponseDto = z.object({
  url: z.string().optional().nullable(),
  alt: z.string().optional().nullable(),
  ratio: z.string().optional().nullable(),
  height: z.number().optional().nullable(),
  width: z.number().optional().nullable(),
  focus: z.object({ x: z.string(), y: z.string() }).optional().nullable(),
});
export type ImageResponseDto = z.infer<typeof ImageResponseDto>;

export const VideoResponseDto = z.object({
  url: z.string().optional().nullable(),
});
export type VideoResponseDto = z.infer<typeof VideoResponseDto>;

export const MediaDto = z
  .object({
    image: ImageResponseDto.optional().nullable(),
    video: VideoResponseDto.optional().nullable(),
    caption: z.string().optional().nullable(),
  })
  .describe('Media information for an entry');
export type MediaDto = z.infer<typeof MediaDto>;

export const ContributorDto = z
  .object({
    name: z.string().optional(),
    position: z.string().optional().nullable(),
    image: ImageResponseDto.optional().nullable(),
  })
  .describe('Contributor information for an entry');
export type ContributorDto = z.infer<typeof ContributorDto>;

// TAXONOMY
export const EntryTaxonomies = z
  .object({
    articleType: z.string().optional().nullable(),
    articleTopics: z.array(z.string()).optional().nullable(),
    articleThemes: z.array(z.string()).optional().nullable(),
    fundCategory: z.array(z.string()).optional().nullable(),
    fundPromotion: z.array(z.string()).optional().nullable(),
    programCountry: z.array(z.string()).optional().nullable(),
    programRegion: z.array(z.string()).optional().nullable(),
    personalizationSegment: z.array(z.string()).optional().nullable(),
    other: z.array(z.string()).optional().nullable(),
    contentOwner: z.array(z.string()).optional().nullable(),
    cmsOrganization: z.array(z.string()).optional().nullable(),
  })
  .describe('Taxonomy information for an article');
export type EntryTaxonomies = z.infer<typeof EntryTaxonomies>;

// PUBLISHED
export const PublishedEntryInput = z
  .object({
    contentfulEntryID: z.string(),
    contentfulType: z.string(),
    adminTitle: z.string(),
    pageTitle: z.string(),
    path: z.string(),
    summary: z.string().optional().nullable(),
    searchKeywords: z.string().optional().nullable(),
    media: MediaDto.optional().nullable(),
    contributors: ContributorDto.array().optional().nullable(),
    template: z.string().optional().nullable(),
    taxonomy: EntryTaxonomies.optional(),
    fund: FundProps.optional().nullable(),
    json: z.string().optional().nullable(),
    html: z.string().optional().nullable(),
    jsonChildren: z.string().optional().nullable(),
    htmlPublished: z.string().optional().nullable(),
    datePublished: z.string().datetime().optional().nullable(),
    dateModified: z.string().datetime().optional().nullable(),
    dateIndexed: z.string().datetime().optional().nullable(),
  })
  .describe('Required fields when indexing a published entry');
export type PublishedEntryInput = z.infer<typeof PublishedEntryInput>;

export const PublishedEntryResponseDto = z
  .object({
    contentfulEntryID: z.string().optional(),
    contentfulType: z.string().optional(),
    adminTitle: z.string().optional().nullable(),
    pageTitle: z.string().optional(),
    path: z.string().optional(),
    summary: z.string().optional().nullable(),
    searchKeywords: z.string().optional().nullable(),
    media: MediaDto.optional().nullable(),
    contributors: ContributorDto.array().optional().nullable(),
    template: z.string().optional().nullable(),
    taxonomy: EntryTaxonomies.optional(),
    fund: FundProps.optional().nullable(),
    json: z.string().optional(),
    html: z.string().optional(),
    jsonChildren: z.string().optional(),
    htmlPublished: z.string().optional(),
    datePublished: z.string().datetime().optional(),
    dateModified: z.string().datetime().optional(),
    dateIndexed: z.string().datetime().optional(),
  })
  .describe('A published entry with optional information');
export type PublishedEntryResponseDto = z.infer<
  typeof PublishedEntryResponseDto
>;

// EDITOR
const PrivatePageDocument = z.object({
  adminTitle: z.string().optional().nullable(),
  pageTitle: z.string().optional().nullable(),
  path: z.string().optional().nullable(),
  summary: z.string().optional().nullable(),
  searchKeywords: z.string().optional().nullable(),
  media: MediaDto.optional().nullable(),
  template: z.string().optional().nullable(),
  taxonomy: EntryTaxonomies.optional().nullable(),
  fund: FundProps.optional().nullable(),
  json: z.string().optional().nullable(),
  parentPageId: z.string().optional().nullable(),
  hasChildren: z.boolean().optional().nullable(),
});
const PrivateContentDocument = z.object({
  adminTitle: z.string().optional().nullable(),
  title: z.string().optional().nullable(),
  subtitle: z.string().optional().nullable(),
  json: z.string().optional().nullable(),
});

export const PrivateEntryInput = z
  .object({
    contentfulEntryID: z.string(),
    contentfulType: z.string(),
    status: z.string(),
    externalDomains: z.string().array().optional(),
    isReferenced: z.boolean(),
    isOrphaned: z.boolean().optional(),
    dateModified: z.string().datetime(),
    dateIndexed: z.string().datetime(),
  })
  .merge(PrivatePageDocument)
  .merge(PrivateContentDocument)
  .describe('Required fields when indexing an private entry');
export type PrivateEntryInput = z.infer<typeof PrivateEntryInput>;

export const PrivateEntryResponseDto = z
  .object({
    contentfulEntryID: z.string(),
    contentfulType: z.string(),
    status: z.string().optional().nullable(),
    externalDomains: z.string().array().optional().nullable(),
    isReferenced: z.boolean().optional().nullable(),
    isOrphaned: z.boolean().optional().nullable(),
    dateModified: z.string().datetime().optional().nullable(),
    dateIndexed: z.string().datetime().optional().nullable(),
  })
  .merge(PrivatePageDocument)
  .merge(PrivateContentDocument)
  .describe('A private entry with optional information');
export type PrivateEntryResponseDto = z.infer<typeof PrivateEntryResponseDto>;

// published
export const searchableFields = [
  'pageTitle.keyword^4',
  'pageTitle.synonyms^2',
  'summary',
  'searchKeywords',
] as const;
export const sortableFields = [
  'pageTitle.keyword',
  'fund.catalogAmount',
  'datePublished',
  'dateModified',
  'dateIndexed',
] as const;
export const facetedFields = [
  'contentfulType.keyword',
  'template.keyword',
  'fund.catalogAmount',
  'taxonomy.articleType.keyword',
  'taxonomy.articleThemes.keyword',
  'taxonomy.articleTopics.keyword',
  'taxonomy.fundCategory.keyword',
  'taxonomy.fundPromotion.keyword',
  'taxonomy.programRegion.keyword',
  'taxonomy.programCountry.keyword',
] as const;

// private
export const searchableFieldsPrivate = [
  'adminTitle^3',
  'pageTitle.keyword^2',
  'summary',
  'searchKeywords',
] as const;
export const sortableFieldsPrivate = [
  'pageTitle.keyword',
  'path.keyword',
  'status.keyword',
  'datePublished',
  'dateModified',
  'dateIndexed',
] as const;
export const facetedFieldsPrivate = [
  'contentfulType.keyword',
  'status.keyword',
  'template.keyword',
  'taxonomy.articleType.keyword',
  'taxonomy.articleThemes.keyword',
  'taxonomy.articleTopics.keyword',
  'taxonomy.fundCategory.keyword',
  'taxonomy.fundPromotion.keyword',
  'taxonomy.programRegion.keyword',
  'taxonomy.programCountry.keyword',
  'externalDomains.keyword',
] as const;

export const facetedFieldNames = {
  'contentfulType.keyword': 'Content Type',
  'template.keyword': 'Template',
  'taxonomy.articleType.keyword': 'Article Type',
  'taxonomy.articleThemes.keyword': 'Article Theme',
  'taxonomy.articleTopics.keyword': 'Article Topic',
  'taxonomy.fundCategory.keyword': 'Fund Category',
  'taxonomy.fundPromotion.keyword': 'Fund Promotion',
  'taxonomy.programRegion.keyword': 'Program Region',
  'taxonomy.programCountry.keyword': 'Program Country',
  'externalDomains.keyword': 'External Domains',
} as const;

// INPUTS
export const IndexSettingsInput = z.object({
  index: z.enum(osIndexEnum),
});
export type IndexSettingsInput = z.infer<typeof IndexSettingsInput>;

export const IndexInput = z.object({
  contentfulEntryID: z.string(),
  contentfulType: z.string(),
  isManual: z.boolean().optional(),
});
export type IndexInput = z.infer<typeof IndexInput>;

// ─── Semantic Input Schemas ───────────────────────────────────────────────────

export const SortDirection = z.enum(['asc', 'desc']);
export type SortDirection = z.infer<typeof SortDirection>;

export const PublishedSortField = z.enum([
  'datePublished',
  'dateModified',
  'dateIndexed',
  'pageTitle',
  'catalogAmount',
]);
export type PublishedSortField = z.infer<typeof PublishedSortField>;

export const PrivateSortField = z.enum([
  'datePublished',
  'dateModified',
  'dateIndexed',
  'pageTitle',
  'path',
  'status',
]);
export type PrivateSortField = z.infer<typeof PrivateSortField>;

const PaginationBase = z.object({
  query: z.string().max(200).optional(),
  offset: z.coerce.number().int().min(0).optional(),
  perPage: z.coerce.number().int().min(0).max(1000).optional(),
  sortDir: SortDirection.optional(),
});

export const ArticleSearchInput = PaginationBase.extend({
  sort: PublishedSortField.optional(),
  articleType: z.string().optional(),
  topics: z.array(z.string()).optional(),
  themes: z.array(z.string()).optional(),
  country: z.string().optional(),
  /** Boost (should) variants — rank matching docs higher without filtering. */
  boostTopics: z.array(z.string()).optional(),
  boostThemes: z.array(z.string()).optional(),
  boostCountry: z.string().optional(),
  promoted: z.boolean().optional(),
  excludeEntryId: z.string().optional(),
});
export type ArticleSearchInput = z.infer<typeof ArticleSearchInput>;

export const CatalogSearchInput = PaginationBase.extend({
  sort: PublishedSortField.optional(),
  fundCategories: z.array(z.string()).optional(),
  fundPromotion: z.string().optional(),
  /** Boost (should) variants — rank matching docs higher without filtering. */
  boostFundPromotion: z.string().optional(),
  priceMin: z.number().optional(),
  priceMax: z.number().optional(),
});
export type CatalogSearchInput = z.infer<typeof CatalogSearchInput>;

export const PublishedSearchInput = PaginationBase.extend({
  sort: PublishedSortField.optional(),
  articleType: z.string().optional(),
});
export type PublishedSearchInput = z.infer<typeof PublishedSearchInput>;

export const PrivateSearchInput = PaginationBase.extend({
  sort: PrivateSortField.optional(),
  contentTypes: z.array(z.string()).optional(),
  parentPageId: z.string().optional(),
  status: z.string().optional(),
  isOrphaned: z.boolean().optional(),
  externalDomain: z.string().optional(),
  aggs: z.enum(facetedFieldsPrivate).array().optional(),
});
export type PrivateSearchInput = z.infer<typeof PrivateSearchInput>;

// RESPONSE
export const SearchResultFacets = z
  .object({
    field: z.enum(facetedFields),
    values: z.object({ key: z.string(), count: z.number() }).array(),
  })
  .optional()
  .array();
export type SearchResultFacets = z.infer<typeof SearchResultFacets>;

export const SearchResultFacetsPrivate = z
  .object({
    // todo: can we make this use a plain string to be more flexible?
    field: z.enum(facetedFieldsPrivate),
    values: z.object({ key: z.string(), count: z.number() }).array(),
  })
  .optional()
  .array();
export type SearchResultFacetsPrivate = z.infer<
  typeof SearchResultFacetsPrivate
>;

export const QueryResponsePublished = z.object({
  items: PublishedEntryResponseDto.array(),
  totalCount: z.number().optional(),
  facets: SearchResultFacets,
});
export type QueryResponsePublished = z.infer<typeof QueryResponsePublished>;

export const QueryResponsePrivate = z.object({
  items: PrivateEntryResponseDto.array(),
  totalCount: z.number().optional(),
  facets: SearchResultFacetsPrivate,
});
export type QueryResponsePrivate = z.infer<typeof QueryResponsePrivate>;

// Tag counts (exhaustive aggregation, size 100 per taxonomy). Used by the
// sitemap so long-tail tags aren't truncated by the top-20 search facets.
export const TagCountsInput = z.object({
  articleType: z.string().optional(),
});
export type TagCountsInput = z.infer<typeof TagCountsInput>;

export const TagCount = z.object({
  tag: z.string(),
  count: z.number(),
});
export type TagCount = z.infer<typeof TagCount>;

export const TagCountsResult = z.object({
  topics: TagCount.array(),
  themes: TagCount.array(),
  countries: TagCount.array(),
});
export type TagCountsResult = z.infer<typeof TagCountsResult>;

export const MutationResponseStandard = z.object({
  success: z.boolean(),
  error: z.string().nullable().optional(),
});
export type MutationResponseStandard = z.infer<typeof MutationResponseStandard>;

// COMMON
export const SearchResultProps = z.object({
  type: z.string().optional(),
  title: z.string().optional(),
  summary: z.string().optional(),
  searchKeywords: z.string().optional(),
  path: z.string().optional(),
  publishDate: z.string().optional(),
  media: MediaProps.optional(),
  fund: FundProps.optional().nullable(),
  contributors: ContributorProps.array().optional(),
  taxonomyArticleType: z.string().optional(),
  taxonomyArticleTheme: z.string().array().optional(),
  taxonomyTopic: z.string().array().optional(),
  taxonomyFundPromotion: z.string().array().optional(),
  taxonomyFundCategory: z.string().array().optional(),
  taxonomyProgramCountry: z.string().array().optional(),
  taxonomyProgramRegion: z.string().array().optional(),
  taxonomyCmsOrganization: z.string().array().optional(),
});
export type SearchResultProps = z.infer<typeof SearchResultProps>;

// VOLUNTEER

/** Input for the volunteer search query (semantic, no OpenSearch DSL). */
export const VolunteerSearchInput = z.object({
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  sort: z.enum(['relevance', 'distance', 'date']).catch('relevance'),
  query: z.string().optional(),
  offset: z.number().int().min(0).optional(),
  perPage: z.number().int().min(1).optional(),
  slug: z.string().optional(),
});
export type VolunteerSearchInput = z.infer<typeof VolunteerSearchInput>;

/** Input for getting a volunteer location by eventId. */
export const GetVolunteerByIdInput = z.object({
  eventId: z.string().min(1),
});
export type GetVolunteerByIdInput = z.infer<typeof GetVolunteerByIdInput>;

/** Input for getting a volunteer location by slug. */
export const GetVolunteerBySlugInput = z.object({
  slug: z.string().min(1),
});
export type GetVolunteerBySlugInput = z.infer<typeof GetVolunteerBySlugInput>;

/** Input for indexing a volunteer location entry. */
export const IndexVolunteerLocationInput = z.object({
  eventId: z.string().min(1),
});
export type IndexVolunteerLocationInput = z.infer<
  typeof IndexVolunteerLocationInput
>;

/** Zod schema for validating volunteer location items returned from OpenSearch. */
export const VolunteerLocationItemDto = z.object({
  eventId: z.string(),
  slug: z.string().nullable().optional(),
  locationName: z.string(),
  // tourId groups a tour; tourName is its display name and slug source.
  tourId: z.string().nullable().optional(),
  tourName: z.string().nullable().optional(),
  // Tour-level description and date range, shared across the tour's locations.
  tourDescription: z.string().nullable().optional(),
  tourStartDate: z.string().nullable().optional(),
  tourEndDate: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  address1: z.string().nullable().optional(),
  address2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  postalCode: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  startDate: z.string(),
  endDate: z.string(),
  point: z.object({ lat: z.number(), lon: z.number() }).nullable().optional(),
  openSlots: z.number(),
  totalSlots: z.number(),
  roles: z.array(z.string()).optional().default([]),
  status: z.string(),
  dateIndexed: z.string().nullable().optional(),
  dressCode: z.string().nullable().optional(),
  dressCodeDetails: z.string().nullable().optional(),
  specialEventDetails: z.string().nullable().optional(),
  partnerWebsite: z.string().nullable().optional(),
  lastModifiedDate: z.string().nullable().optional(),
  volunteerCoordinatorName: z.string().nullable().optional(),
  volunteerCoordinatorEmail: z.string().nullable().optional(),
  eventContactName: z.string().nullable().optional(),
  eventContactEmail: z.string().nullable().optional(),
  eventContactPhone: z.string().nullable().optional(),
  venueName: z.string().nullable().optional(),
  image: z.object({ url: z.string() }).optional(),
  tourCount: z.number().optional(),
  /** Summary of tour events — inner_hits from OpenSearch. */
  tourEvents: z
    .array(
      z.object({
        eventId: z.string().optional(),
        city: z.string().nullable().optional(),
        state: z.string().nullable().optional(),
        startDate: z.string().optional(),
        endDate: z.string().optional(),
        openSlots: z.number().nullable().optional(),
        totalSlots: z.number().nullable().optional(),
      }),
    )
    .optional(),
});
export type VolunteerLocationItemDto = z.infer<typeof VolunteerLocationItemDto>;

/** Zod schema for the full volunteer search response. */
export const VolunteerLocationsResponseDto = z.object({
  items: z.array(VolunteerLocationItemDto),
  totalCount: z.number().optional(),
});
export type VolunteerLocationsResponseDto = z.infer<
  typeof VolunteerLocationsResponseDto
>;

/** Zod schema for a postal code autocomplete result. */
export const PostalCodeResultDto = z.object({
  postalCode: z.string(),
  city: z.string(),
  state: z.string(),
  lat: z.number(),
  lng: z.number(),
});
export type PostalCodeResultDto = z.infer<typeof PostalCodeResultDto>;

// GEO

/** Common geo location shape used across the codebase. */
export const GeoLocationDto = z.object({
  city: z.string().optional(),
  state: z.string().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
});
export type GeoLocationDto = z.infer<typeof GeoLocationDto>;

/** Response shape from the `/api/geo` endpoint. */
export const GeoApiResponseDto = z.object({
  countryCode: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
});
export type GeoApiResponseDto = z.infer<typeof GeoApiResponseDto>;

/** Lat/lng coordinate pair. */
export const GeoCoordinatesDto = z.object({
  lat: z.number(),
  lng: z.number(),
});
export type GeoCoordinatesDto = z.infer<typeof GeoCoordinatesDto>;

/** Zod schema for a single role slot count. */
export const RoleSlotCountDto = z.object({
  role: z.string(),
  openSlots: z.number().int().min(0),
  totalSlots: z.number().int().min(0),
});
export type RoleSlotCountDto = z.infer<typeof RoleSlotCountDto>;

/** Zod schema for a single volunteer slot entry. */
export const VolunteerSlotEntryDto = z.object({
  id: z.string(),
  eventLocationId: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  status: z.string(),
  description: z.string().nullable().optional(),
});
export type VolunteerSlotEntryDto = z.infer<typeof VolunteerSlotEntryDto>;

/** Zod schema for live volunteer slot data (response from connect-proxy). */
export const LiveSlotDataDto = z.object({
  totalOpenSlots: z.number().int().min(0),
  totalSlots: z.number().int().min(0),
  roleCounts: z.array(RoleSlotCountDto),
  slots: z.array(VolunteerSlotEntryDto),
});
export type LiveSlotDataDto = z.infer<typeof LiveSlotDataDto>;

/** Status derived from slot availability and event proximity. */
export enum VolunteerEventStatus {
  Open = 'open',
  Urgent = 'urgent',
  Waitlist = 'waitlist',
}

/** The document shape indexed into the volunteer OpenSearch index. */
export const VolunteerLocationDocumentDto = z.object({
  eventId: z.string(),
  slug: z.string(),
  locationName: z.string(),
  // tourId groups a tour; tourName is its display name and slug source.
  tourId: z.string().nullable(),
  tourName: z.string().nullable(),
  // Tour-level description and date range, shared across the tour's locations.
  tourDescription: z.string().nullable(),
  tourStartDate: z.string().nullable(),
  tourEndDate: z.string().nullable(),
  description: z.string().nullable(),
  address1: z.string().nullable(),
  address2: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  postalCode: z.string().nullable(),
  country: z.string().nullable(),
  startDate: z.string(),
  endDate: z.string(),
  point: z.object({ lat: z.number(), lon: z.number() }).nullable(),
  openSlots: z.number(),
  totalSlots: z.number(),
  roles: z.array(z.string()),
  status: z.nativeEnum(VolunteerEventStatus),
  dateIndexed: z.string(),
  dressCode: z.string().nullable(),
  dressCodeDetails: z.string().nullable(),
  specialEventDetails: z.string().nullable(),
  partnerWebsite: z.string().nullable(),
  lastModifiedDate: z.string().nullable(),
  volunteerCoordinatorName: z.string().nullable(),
  volunteerCoordinatorEmail: z.string().nullable(),
  eventContactName: z.string().nullable(),
  eventContactEmail: z.string().nullable(),
  eventContactPhone: z.string().nullable(),
  venueName: z.string().nullable(),
  image: z.object({ url: z.string() }),
});
export type VolunteerLocationDocumentDto = z.infer<
  typeof VolunteerLocationDocumentDto
>;

/** Zod schema for the autocomplete postal code response. */
export const AutocompletePostalCodeResponseDto = z.object({
  items: z.array(PostalCodeResultDto),
});
export type AutocompletePostalCodeResponseDto = z.infer<
  typeof AutocompletePostalCodeResponseDto
>;
